import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useFullScreenHandle } from 'react-full-screen';
import { HotkeysProvider } from 'react-hotkeys-hook';
import { Resizable } from 're-resizable';
import cx from 'classnames';
import lodash from 'lodash';

import { ClipFavouritesProvider } from '../ClipFavourites/context';
import { ClipFilterProvider, useClipFilter } from '../ClipFilter/context';
import { VideoDrawingProvider, useDrawingMarkers } from '../VideoDrawing/context';
import {
  setShowSpeedOption,
  updateAdditionalAngleUploadFeed,
  updateCurrentFeed,
  updateCurrentVideoThatInFrame,
  updateSelectedVideoQuality,
  updateShowWarningModal,
} from '../../store/sharedStore/sharedStoreSlice';
import {
  buildQueryString,
  getVideoQualityFromLocalStorage,
  parseQueryString,
  setVideoQualityToLocalStorage,
  useDimensions,
} from '../../helpers';
import { updateVideoControls } from '../../store/videos/videoSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import VideoQualitySelector from '../modules/VideoQualitySelector';
import qualityMenuStyles from '../modules/VideoQualitySelector/VideoQualitySelector.module.scss';
import { findSearchValue } from '../../helpers/getSearchValue';
import { ACCESS_TYPE_ENUM } from '../../enums/ACCESS_ENUM';
import { RolesLowerCase } from '../../enums/ROLES';
import { UploadingStatus } from '../../store/sharedStore/sharedStoreSlice';
import FullPageLoader from '../modules/FullPageLoader';
import VideoComments, { SidebarToggle } from '../modules/VideoComments';
import { speedOptions } from '../ComposerVideoPage';
import VideoPlayer from '../modules/VideoPlayer';
import { TClip } from '../Timeline/TimelineClip';
import styles from './VideoPage.module.scss';
import { STATUS, VIDEOFEED_STATUS, VIDEOFEED_TYPE } from '../../enums/ENUMS';
import { VIDEOFEED } from '../../enums';
import Timeline from '../Timeline';
import { getComposerDataByVideoFeedId, getComposerDataByVideoFeedIdForVideoPage } from '../../api';
import { checkContentAvailability } from '../../api/accessesApi';

export type TRow = {
  id: string;
  name: string;
  clips: TClip[];
};

const VideoPage: React.FC = () => {
  const fullScreen = useFullScreenHandle();
  const { videoFeed, status, videoLength, videoControls } = useAppSelector((state) => state.videoStore);
  const { showSidebar, showSpeedOption, currentFeed, uploadingContentState } = useAppSelector((state) => state.sharedStore);
  const { refetchFeedList } = useAppSelector((state) => state.videoFeedsListStore);
  const { currentUser } = useAppSelector((state) => state.user);
  const { heightD, widthD } = useDimensions();
  const feedId = findSearchValue('feedId');
  const fileside = findSearchValue('fileside');
  const shared = findSearchValue('shared');
  const dispatch = useAppDispatch();
  const [userAccess, setUserAccess] = useState<ACCESS_TYPE_ENUM | 'idle'>('idle');
  const [ready, setReady] = useState(false);
  const [fullPageLoader, setFullPageLoader] = useState(false);
  const isContentAdminOA = findSearchValue('allContent') === 'true' && currentUser?.role?.title === RolesLowerCase.ORGANIZATION_ADMIN;
  const lastRefetchFeedListRef = useRef(refetchFeedList);

  const [videoRows, setVideoRows] = useState<TRow[]>([]);

  const runCheck = async (type: 'folder' | 'videofeed', id: string) => {
    const allContent = findSearchValue('allContent');
    const parentFolders = findSearchValue('parentFolders');
    if (allContent === 'true' || parentFolders === 'true') return;
    try {
      const res = await checkContentAvailability(type, id);
      if (res.data?.deleted) {
        dispatch(
          updateShowWarningModal({ show: true, warningText: 'The content you are trying to access has been deleted', redirect: true }),
        );
      } else if (res.data?.noAccess) {
        dispatch(
          updateShowWarningModal({
            show: true,
            warningText: 'You do not have permission to access the content you are trying to view.',
            redirect: true,
          }),
        );
      } else if (res?.data?.differentOrg) {
        dispatch(
          updateShowWarningModal({
            show: true,
            warningText: `Your current organisation is different from the content's organisation.${
              res?.data?.organizationName ? ` The content is from ${res.data.organizationName}.` : ''
            }`,
            redirect: true,
          }),
        );
      }
    } catch (err) {
      console.error(err);
    }
  };

  const fetchFeedData = async (id: string, ignoreCache?: boolean, background = false) => {
    try {
      if (!background) setFullPageLoader(true);
      const res = await getComposerDataByVideoFeedIdForVideoPage(id, ignoreCache ? { cache: false } : undefined);
      const feedData = res.data;
      setReady(true);
      dispatch(updateCurrentFeed(feedData));
      if (isContentAdminOA) {
        setUserAccess(ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE);
      } else if (res?.data?.highest_user_access?.access?.title) {
        setUserAccess(res.data.highest_user_access.access.title as ACCESS_TYPE_ENUM);
      }
      runCheck('videofeed', id);
      if (shared === 'true' && currentUser && +res?.data?.created_by?.id === +currentUser?.id) {
        const urlObject = parseQueryString(window.location.search);
        if (urlObject?.shared) {
          delete urlObject?.shared;
        }
        window.history.replaceState(null, '', `?${buildQueryString(urlObject)}`);
        window.location.reload();
      }
      return feedData as VIDEOFEED;
    } catch (err: any) {
      console.log(err);
    } finally {
      if (!background) setFullPageLoader(false);
    }
  };

  const [savedFeed, setFeedData] = useState<any>(null);

  const parseCustomTopics = (feedData: any, videoLength: number) => {
    const customTopics = lodash.cloneDeep(feedData.topics);
    customTopics.forEach((row: any, rowIndex: number) => {
      row.clips.forEach((clip: any, clipIndex: number) => {
        customTopics[rowIndex].clips[clipIndex].topicId = row.id;
        if (clip.time_start <= 0 && clip.time_end <= videoLength) {
          customTopics[rowIndex].clips[clipIndex].time_start = 0.001;
        } else if (clip.time_start <= 0 && clip.time_end > videoLength) {
          customTopics[rowIndex].clips[clipIndex].time_start = 0.001;
          customTopics[rowIndex].clips[clipIndex].time_end = videoLength;
        } else if (clip.time_start >= 0 && clip.time_start < videoLength && clip.time_end > videoLength) {
          customTopics[rowIndex].clips[clipIndex].time_end = videoLength;
        }
      });

      customTopics[rowIndex].clips = customTopics[rowIndex].clips.filter((c: any) => c.time_start >= 0 && c.time_end <= videoLength);
    });

    customTopics.forEach((row: any, rowIndex: number) => {
      customTopics[rowIndex].clips = row.clips
        .filter((clip: any) => clip.time_end >= 0)
        .sort((a: any, b: any) => a.time_start - b.time_start);
    });

    setVideoRows(customTopics as TRow[]);
  };

  // useEffect to fetch data only when feedId changes, or for json data changes
  const fetchData = async (forJson?: boolean): Promise<any> => {
    if (feedId && feedId !== savedFeed?.id) {
      const feedData = await fetchFeedData(feedId);
      if (feedData) {
        setFeedData(feedData);
      }
      return feedData;
    } else if (forJson && feedId) {
      const feedData = await fetchFeedData(feedId, true);
      if (feedData) {
        setFeedData(feedData);
      }
      return feedData;
    }
  };
  useEffect(() => {
    const v = videoFeed.find((v) => v?.id == feedId);
    if (isContentAdminOA) {
      setUserAccess(ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE);
    } else if (v?.highest_user_access?.access?.title) {
      setUserAccess(v.highest_user_access.access.title as ACCESS_TYPE_ENUM);
    }

    fetchData();
  }, [feedId, savedFeed, videoFeed, isContentAdminOA]);

  useEffect(() => {
    if (!feedId || lastRefetchFeedListRef.current === refetchFeedList) {
      return;
    }

    lastRefetchFeedListRef.current = refetchFeedList;
    void (async () => {
      const feedData = await fetchFeedData(feedId, true, true);
      if (feedData) {
        setFeedData(feedData);
      }
    })();
  }, [feedId, refetchFeedList]);

  useEffect(() => {
    if (videoLength > 0 && savedFeed) {
      parseCustomTopics(savedFeed, videoLength / 1000);
    }
  }, [videoLength, savedFeed]);

  const { sidebarVisible: showCommentsSection, setSidebarVisible: setShowCommentSection } = useClipFilter();
  const { drawingMinHeight } = useDrawingMarkers();
  const lastWideCommentsState = useRef(true);
  const commentsAutoHidden = useRef(false);
  const switchCommentsView = useCallback(() => {
    setShowCommentSection(!showCommentsSection);
  }, [showCommentsSection]);

  const [videoPlayerHeight, setVideoPlayerHeight] = useState(650);
  const [timelineCollapsed, setTimelineCollapsed] = useState(false);
  const [mobileVideoContainerHeight, setMobileVideoContainerHeight] = useState<number | null>(null);
  const manuallyResizedVideoRef = useRef(false);
  const mobileSidebarWidth = 74;
  const mobileWorkspaceWidth = Math.max(0, widthD - mobileSidebarWidth);
  const desktopWorkspaceWidth = Math.max(0, widthD - (showSidebar ? 303 : 74) - (showCommentsSection ? 315 : 50));
  const mobileVideoHeight = Math.max(140, Math.round((mobileWorkspaceWidth * 9) / 16));
  const maxVideoContainerHeight = Math.max(140, drawingMinHeight, heightD - 62 - 120);
  const shortViewportVideoCap = Math.min(
    Math.max(180, heightD - 62 - 260),
    Math.max(180, Math.round((desktopWorkspaceWidth * 9) / 16)),
  );
  const resolvedVideoContainerHeight = Math.max(drawingMinHeight,
    widthD < 768
      ? Math.min(mobileVideoContainerHeight ?? mobileVideoHeight, maxVideoContainerHeight)
      : heightD < 900 && !manuallyResizedVideoRef.current
        ? Math.min(videoPlayerHeight + 69, shortViewportVideoCap)
        : videoPlayerHeight + 69);
  const resolvedVideoPlayerHeight =
    widthD < 768 ? resolvedVideoContainerHeight : Math.max(60, resolvedVideoContainerHeight - 69);

  useEffect(() => {
    if (widthD >= 1024 && !commentsAutoHidden.current) {
      lastWideCommentsState.current = showCommentsSection;
    }
  }, [showCommentsSection, widthD]);

  useEffect(() => {
    if (widthD < 1024) {
      if (!commentsAutoHidden.current) {
        lastWideCommentsState.current = showCommentsSection;
        commentsAutoHidden.current = true;
      }
      setShowCommentSection(false);
    } else if (commentsAutoHidden.current) {
      commentsAutoHidden.current = false;
      setShowCommentSection(lastWideCommentsState.current);
    }
  }, [widthD]);

  const [prevRowsLength, setPrevRowsLength] = useState(0);
  const [prevHeight, setPrevHeight] = useState(0);
  useEffect(() => {
    if (manuallyResizedVideoRef.current) {
      setPrevRowsLength(videoRows.length);
      setPrevHeight(heightD);
      return;
    }
    if (prevRowsLength === 0 && videoRows.length === 0) {
      const windowHeight = window.innerHeight - 243;
      setVideoPlayerHeight(windowHeight);
    }
    if (prevRowsLength !== videoRows.length || heightD !== prevHeight) {
      if (videoRows.length > 5) {
        // 70 and 240 are height of header and videoplay controls + videorows
        const windowHeight = window.innerHeight - 70 - 190;
        setVideoPlayerHeight(windowHeight - 120);
      } else if (videoRows.length <= 5 && videoRows.length > 1) {
        const windowHeight = window.innerHeight - 108 - 129;
        const rowsHeight = videoRows.length * 32;
        const heightForPlayer = windowHeight - rowsHeight;
        setVideoPlayerHeight(heightForPlayer);
      } else if (videoRows.length === 1) {
        const windowHeight = window.innerHeight - 45 - 220;
        setVideoPlayerHeight(windowHeight);
      } else {
        const windowHeight = window.innerHeight - 243;
        setVideoPlayerHeight(windowHeight);
      }
      setPrevRowsLength(videoRows.length);
    }
    setPrevHeight(heightD);
  }, [videoRows, heightD]);

  const onVideoQualitySet = (videoQualityArr: [string, string]) => {
    // videoQualityArr: ['1080', 'www.video.com/video.mp4']
    dispatch(updateSelectedVideoQuality({ quality: videoQualityArr[0], url: videoQualityArr[1] }));
    setVideoQualityToLocalStorage(`${videoQualityArr[0]}`);
  };

  const isFolderCollaborator =
    !!currentFeed?.folder &&
    !!currentUser &&
    ((currentFeed.folder.collaborators || []).some((user) => user.email === currentUser.email) ||
      (currentFeed.folder.group_collaborators || []).some((group) =>
        [...(group.analysts || []), ...(group.viewers || [])].some((user) => user.email === currentUser.email),
      ));
  const pendingAdditionalAngleCount =
    uploadingContentState.filter(
      (item) =>
        String(item?.videofeedId) === String(currentFeed?.id) &&
        item?.uploadMode === 'additional-angle' &&
        item?.status !== UploadingStatus.FAILED &&
        item?.status !== UploadingStatus.FINISHED,
    ).length;
  const hasUploadingAdditionalAngle = uploadingContentState.some(
    (item) =>
      String(item?.videofeedId) === String(currentFeed?.id) &&
      item?.uploadMode === 'additional-angle' &&
      item?.status === UploadingStatus.UPLOADING,
  );
  const hasPendingAdditionalAngle = pendingAdditionalAngleCount > 0;
  const canUploadAdditionalAngles =
    !!currentFeed &&
    currentFeed.type !== VIDEOFEED_TYPE.COMPOSER &&
    currentFeed.type !== VIDEOFEED_TYPE.MY_COMPOSER &&
    (currentFeed.status === VIDEOFEED_STATUS.DONE || hasPendingAdditionalAngle) &&
    ((currentFeed?.videos?.length || 0) + pendingAdditionalAngleCount < 4 || hasUploadingAdditionalAngle) &&
    (isContentAdminOA ||
      currentFeed?.created_by?.id === currentUser?.id ||
      currentFeed?.folder?.owner?.id === currentUser?.id ||
      isFolderCollaborator ||
      userAccess === ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE);

  const handlePlayerContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    if (canUploadAdditionalAngles && currentFeed) {
      e.preventDefault();
      dispatch(updateAdditionalAngleUploadFeed(currentFeed));
    }
  };

  const clipIndexRegex = findSearchValue('clip');
  useEffect(() => {
    const clipIndex = clipIndexRegex ? Number(clipIndexRegex) : NaN;
    const selectedVideo =
      currentFeed?.videos?.[Number.isFinite(clipIndex) ? clipIndex : 0] ||
      currentFeed?.videos?.[0];

    if (currentFeed && selectedVideo) {
      dispatch(updateCurrentVideoThatInFrame(selectedVideo));
    }
    const rememberedQuality = getVideoQualityFromLocalStorage(); // string like "1080"
    if (currentFeed && selectedVideo) {
      if (
        clipIndexRegex &&
        rememberedQuality?.length &&
        selectedVideo.s3_keys[rememberedQuality] &&
        selectedVideo.s3_keys[rememberedQuality]?.length
      ) {
        onVideoQualitySet([rememberedQuality, selectedVideo.s3_keys[rememberedQuality]]); //['1080', 'www.video.com/video.mp4']
      } else if (clipIndexRegex) {
        let highestKey = null;
        let highestValue = null;
        for (const key in selectedVideo.s3_keys) {
          if ((!highestKey || parseInt(key) > parseInt(highestKey)) && selectedVideo.s3_keys[key].length) {
            highestKey = key;
            highestValue = selectedVideo.s3_keys[key];
          }
        }
        if (highestKey && highestValue) {
          onVideoQualitySet([highestKey, highestValue]);
        }
      }
    }
  }, [currentFeed]);

  const [speedPos, setSpeedPos] = useState({ top: 0, left: 0 });

  const [showMobileFeedFlash, setShowMobileFeedFlash] = useState(false);
  const [mobileFeedFlashTitle, setMobileFeedFlashTitle] = useState('');
  const previousMobileFeedIdRef = useRef<string | null>(null);
  const mobileFeedFlashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (mobileFeedFlashTimeoutRef.current) {
        clearTimeout(mobileFeedFlashTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!feedId) return;

    if (widthD >= 768) {
      previousMobileFeedIdRef.current = feedId;
      return;
    }

    if (previousMobileFeedIdRef.current === null) {
      previousMobileFeedIdRef.current = feedId;
      return;
    }

    if (previousMobileFeedIdRef.current !== feedId && currentFeed) {
      previousMobileFeedIdRef.current = feedId;
      setMobileFeedFlashTitle(currentFeed.title);
      setShowMobileFeedFlash(true);

      if (mobileFeedFlashTimeoutRef.current) {
        clearTimeout(mobileFeedFlashTimeoutRef.current);
      }

      mobileFeedFlashTimeoutRef.current = setTimeout(() => {
        setShowMobileFeedFlash(false);
      }, 2000);
    }
  }, [feedId, widthD, currentFeed]);

  useEffect(() => {
    const speedOptionMenuRef = document.querySelector('#speedOptionMenuRef');
    if (speedOptionMenuRef) {
      const rect = speedOptionMenuRef.getBoundingClientRect();
      setSpeedPos({ top: rect.top, left: rect.left });
    }
  }, [showSpeedOption, feedId, videoRows]);

  const selectSpeed = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      dispatch(updateVideoControls({ ...videoControls, playbackRate: Number(e.currentTarget.id) }));
      setTimeout(() => {
        dispatch(setShowSpeedOption(false));
      }, 300);
    },
    [videoControls, dispatch, showSpeedOption],
  );

  const renderPlayback = useMemo(() => {
    return (
      <div
        className={qualityMenuStyles.playbackMenu}
        style={{
          color: 'white',
          position: 'absolute',
          zIndex: 9999,
          top: `${speedPos.top - 200}px`,
          left: `${speedPos.left}px`,
          display: showSpeedOption ? 'flex' : 'none',
        }}
        id={'speed_selector'}
        onMouseLeave={() => dispatch(setShowSpeedOption(false))}
      >
        {speedOptions.map((speed) => (
          <div
            key={speed}
            onClick={showSpeedOption ? selectSpeed : console.log}
            id={speed.toString()}
            className={cx(qualityMenuStyles.option, videoControls.playbackRate === speed && qualityMenuStyles.selectedOption)}
          >
            {speed === 1.0 ? 'Normal' : speed}
          </div>
        ))}
      </div>
    );
  }, [showSpeedOption, speedPos]);

  return (
    <div className={cx(styles.videoPage, showSidebar && styles.expandedSidebarOffset)}>
      {widthD < 768 && showMobileFeedFlash && (
        <div className={styles.mobileFeedFlash} role="status" aria-live="polite">
          <div className={styles.mobileFeedFlashLabel}>Opened feed</div>
          <div className={styles.mobileFeedFlashTitle}>{mobileFeedFlashTitle}</div>
        </div>
      )}
      <div className={styles.hiddenQualityHelper}>
        {currentFeed && currentFeed?.videos[0] && <VideoQualitySelector videoData={currentFeed?.videos[0]} />}
      </div>
      <HotkeysProvider>
        {fullPageLoader && <FullPageLoader loading />}
        {status === STATUS.LOADING && !fileside && savedFeed?.id !== feedId ? (
          <FullPageLoader loading />
        ) : (
          <>
            <div
              className={cx(
                styles.videoPageContent,
                timelineCollapsed && styles.timelineCollapsed,
                !showCommentsSection && styles.videoPageContentFullWidth,
                widthD < 768 && showCommentsSection && styles.mobileCommentsOpen,
              )}
            >
              <Resizable
                size={{ height: resolvedVideoContainerHeight, width: '100%' }}
                onResizeStart={(e, direction, ref) => {
                  if (timelineCollapsed) {
                    if (widthD < 768) setMobileVideoContainerHeight(ref.offsetHeight);
                    else setVideoPlayerHeight(Math.max(60, ref.offsetHeight - 69));
                    setTimelineCollapsed(false);
                  }
                  if (widthD >= 768) manuallyResizedVideoRef.current = true;
                }}
                onResizeStop={(e, direction, ref, d) => {
                  if (widthD < 768) {
                    setMobileVideoContainerHeight(Math.min(maxVideoContainerHeight, Math.max(140, ref.offsetHeight)));
                  } else {
                    setVideoPlayerHeight(Math.max(60, ref.offsetHeight - 69));
                  }
                }}
                minHeight={Math.max(drawingMinHeight, widthD < 768 ? 140 : 60)}
                maxHeight={!timelineCollapsed && widthD < 768 ? maxVideoContainerHeight : undefined}
                handleClasses={{
                  right: 'pointer-events-none',
                  topRight: 'pointer-events-none',
                  bottomRight: 'pointer-events-none',
                  bottomLeft: 'pointer-events-none',
                  topLeft: 'pointer-events-none',
                  left: 'pointer-events-none',
                }}
                bounds={'parent'}
                enable={{
                  top: true,
                  right: false,
                  bottom: true,
                  left: false,
                  topRight: false,
                  bottomRight: false,
                  bottomLeft: false,
                  topLeft: false,
                }}
                handleComponent={{
                  bottom: (
                    <div
                      style={{
                        zIndex: 50,
                        position: 'absolute',
                        width: widthD < 768 ? '100%' : '100vw',
                        right: '0px',
                        top: widthD < 768 ? '-9px' : '0px',
                        height: widthD < 768 ? '28px' : '10px',
                        cursor: widthD < 768 ? 'row-resize' : undefined,
                        touchAction: widthD < 768 ? 'none' : undefined,
                      }}
                    />
                  ),
                }}
              >
                <div className={styles.videoPlayer} onContextMenu={handlePlayerContextMenu}>
                  {currentFeed && (
                    <VideoPlayer
                      videoRows={videoRows}
                      videoPlayerHeight={resolvedVideoPlayerHeight}
                      fullScreen={fullScreen}
                      currentFeed={currentFeed}
                      ready={ready}
                    />
                  )}
                </div>
              </Resizable>
              <div className={styles.videoMenu}>
                <div
                  style={{
                    width:
                      widthD < 768
                        ? '100%'
                        : `calc(100vw - ${showSidebar ? '303px' : '74px'} - ${showCommentsSection ? '315px' : '50px'})`,
                  }}
                >
                  <Timeline
                    collapsed={timelineCollapsed}
                    onToggleCollapse={() => setTimelineCollapsed((previous) => !previous)}
                    fetchData={fetchData}
                    onAngleLockChange={(clipId, locked_angle_id) => setFeedData((previous: VIDEOFEED | null) => previous && ({
                      ...previous,
                      topics: previous.topics.map((topic) => ({
                        ...topic,
                        clips: topic.clips.map((clip) => String(clip.id) === String(clipId)
                          ? { ...clip, locked_angle_id } : clip),
                      })),
                    }))}
                    userAccess={userAccess}
                    videoRows={videoRows}
                    videoPlayerHeight={resolvedVideoPlayerHeight}
                    fullScreen={fullScreen}
                    feedData={currentFeed}
                    setVideoRows={setVideoRows}
                    onShowComments={() => setShowCommentSection(true)}
                  />
                </div>
              </div>
            </div>
            <div className={cx(styles.videoComments, !showCommentsSection && styles.hiddenComments)}>
              <div className={styles.videoCommentsPanel} aria-hidden={!showCommentsSection}>
                <VideoComments switchCommentsView={switchCommentsView} />
              </div>
              <div className={styles.videoCommentsHidden} aria-hidden={showCommentsSection}>
                <SidebarToggle expanded={false} onClick={switchCommentsView} />
              </div>
            </div>
          </>
        )}
      </HotkeysProvider>
      {renderPlayback}
    </div>
  );
};

const VideoPageWithFavourites = () => (
  <ClipFavouritesProvider>
    <ClipFilterProvider>
      <VideoDrawingProvider><VideoPage /></VideoDrawingProvider>
    </ClipFilterProvider>
  </ClipFavouritesProvider>
);
export default VideoPageWithFavourites;
